import {
  ReportRecommendation,
  RecommendationReason,
  ConcernReason,
  BacteriaReason,
} from '@snapi/types';
import { Description, Subtitle } from '@snapi/luxury';
import capitalize from 'lodash.capitalize';
import { FoodIcon, BabyFoodIcon } from './images/food-base64';

const reasonToBadge = (
  reason: RecommendationReason,
  categories: string[],
  condition: string[]
): string[] => {
  const badges: string[] = [];

  const { action, name, taxonomy, category } = reason as BacteriaReason;

  if ((reason as ConcernReason).concern === 'diversity') {
    badges.push(`to improve ${(reason as ConcernReason).concern}`);
  }
  if ((reason as ConcernReason).concern === 'balance') {
    badges.push(`to improve gut ${(reason as ConcernReason).concern}`);
  }

  if (categories.includes('condition')) {
    badges.push(...condition);
  }
  if (categories.includes('key')) {
    badges.push(`${name}`);
  }
  if (categories.includes('disruptive')) {
    badges.push(`${name}`);
  }

  return badges;
};

interface ReportRecommendationCardPdfProps {
  value: ReportRecommendation;
  expanded?: boolean;
  onExpand?: () => void;
}

export const ReportRecommendationCardPdf = ({
  value: {
    reason,
    categories,
    condition,
    recommendation: {
      type,
      name,
      reliability,
      references,
      servings,
      category,
      recipes,
      products,
      description,
    },
    score,
  },
  expanded,
  onExpand,
}: ReportRecommendationCardPdfProps) => {
  return (
    <div className="rounded-[10px] relative bg-white shadow-[4px_4px_10px_0_rgba(186, 186, 186, 0.15)] flex flex-1 flex-grow-0 flex-col">
      <div className="flex p-3 border-b border-gray-soft">
        <h5>
          {name} in {category === 'food' ? 'baby' : 'mom'}'s diet
        </h5>
      </div>
      <div className="flex">
        <div className="flex flex-1 flex-col">
          <div className="flex justify-between p-3 gap-3">
            <div className="flex flex-col justify-between">
              <div className="flex flex-col gap-3">
                <div>
                  {' '}
                  <Subtitle>What can it help with?</Subtitle>
                </div>

                  <div className="flex flex-row flex-wrap gap-2">
                  {reasonToBadge(reason, categories || [], condition || []).map(
                    (badgeText, index) => (
                      <span
                        key={index}
                        className="bg-blue rounded-full px-4 pb-4 flex shrink w-max"
                      >
                        <Subtitle>{badgeText}</Subtitle>
                      </span>
                    )
                  )}
                </div>
              </div>
              <div className="flex flex-1" />
            </div>
            <div>{category === 'mother' ? <FoodIcon /> : <BabyFoodIcon />}</div>
          </div>

          <div>
            <div className="flex flex-col px-3 pt-5 pb-3 border-t border-gray-soft ">
              <div className="flex items-end mb-2">
                <span className="bg-blue rounded-full px-4 pb-4 flex">
                  <Subtitle>
                    {capitalize(reliability)} confidence research
                  </Subtitle>
                </span>
              </div>
              <Description>{description}</Description>
            </div>
            {references && references.length > 0 && (
              <div className="flex flex-col p-3 border-t border-gray-soft gap-1 mb-3">
                <h5 className="pb-1">Research</h5>
                {references.map(({ href, text }) => (
                  <a
                    key={href}
                    href={href}
                    className="underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Subtitle>{text}</Subtitle>
                  </a>
                ))}
              </div>
            )}
            {products && products.length > 0 && (
              <div className="flex flex-col p-3 border-t border-gray-soft gap-1">
                <h5 className="pb-1">Products</h5>
                {products.map(({ href, text }) => (
                  <a
                    href={href}
                    key={href}
                    className="underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Subtitle>{text}</Subtitle>
                  </a>
                ))}
              </div>
            )}
            {recipes && recipes.length > 0 && (
              <div className="flex flex-col p-3 border-t border-gray-soft gap-1 mb-3">
                <h5 className="pb-1">Recipes</h5>
                {recipes.map(({ href, text }) => (
                  <a
                    href={href}
                    key={href}
                    className="underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Subtitle>{text}</Subtitle>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
